// "Is the donor subscription live?" — the ONE read the donate wizard unlocks on
// (arc 101 slice 11, plan doc 05).
//
// Two endpoints answer it because the two facts live apart: the tier comes from
// `GET /v1/billing/entitlements` (the Stripe seat sync writes `oss_donor` from
// the donor price, so it is real money) and "collection stopped after a failed
// payment" from `GET /v1/billing/usage`. `canFundDonation` reads both, so the
// client mirror does too — see `lib/donateWizard.ts` for the rule itself.
//
// WHY IT POLLS: checkout hands the browser back long before
// `checkout.session.completed` reaches the worker, and the redirect carries no
// proof (a donor can type `?donated=1`; a closed tab sends nothing). So while a
// caller says it is waiting, this re-asks the server until the server changes its
// mind — the webhook stays the only thing that unlocks anything.

import { createEffect, onCleanup } from 'solid-js';
import { useEntitlements, useUsage } from './billing';
import { type DonorFunding, donorSubscriptionActive } from './donateWizard';
import { SESSION_TOKEN } from './session';

/** How often to re-ask while a payment is in flight — a webhook lands in seconds, not minutes. */
export const FUNDING_POLL_MS = 10_000;

export type DonorFundingRead = {
  /** The server's answer, or null while the tier is unknown. */
  readonly funding: () => DonorFunding | null;
  /** `donorSubscriptionActive` over it — the wizard's unlock. */
  readonly funded: () => boolean;
};

/**
 * Read the donor's funding state, polling while `waiting()` is true.
 *
 * The TIER is required: unknown tier is "not funded", so a step never unlocks
 * during a load. `billing_paused` is not — it only ever tightens the verdict, and
 * a failed usage read must not lock out a donor who is paying (the API re-checks
 * both anyway, and its 402 carries the sentence to render).
 */
export function useDonorFunding(
  waiting: () => boolean,
  token: string | null = SESSION_TOKEN,
): DonorFundingRead {
  const entitlements = useEntitlements(token);
  const usage = useUsage(token);

  const funding = (): DonorFunding | null => {
    const tier = entitlements.data?.tier;
    if (tier === undefined) return null;
    return { tier, billingPaused: usage.data?.billing_paused ?? false };
  };

  createEffect(() => {
    if (!waiting()) return;
    const timer = setInterval(() => {
      void entitlements.refetch();
      void usage.refetch();
    }, FUNDING_POLL_MS);
    onCleanup(() => clearInterval(timer));
  });

  return { funding, funded: () => donorSubscriptionActive(funding()) };
}
